if (!customElements.get("candle-subscription-options")) {
  customElements.define(
    "candle-subscription-options",
    class CandleSubscriptionOptions extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        const dataElement = this.querySelector("[data-subscription-data]");
        const subscriptionData = dataElement?.textContent.trim();
        if (!subscriptionData) {
          if (!this.dataObserver) {
            this.dataObserver = new MutationObserver(() => this.connectedCallback());
            this.dataObserver.observe(this, { childList: true, subtree: true, characterData: true });
          }
          return;
        }

        this.dataObserver?.disconnect();
        this.dataObserver = null;
        this.initialized = true;
        this.data = JSON.parse(subscriptionData);
        this.variantId = String(this.dataset.initialVariantId);
        this.purchaseType = this.data.variants[this.variantId]?.requiresSellingPlan
          ? "subscription"
          : "one-time";
        this.frequencySelect = this.querySelector("[data-frequency-select]");
        this.frequencyPanel = this.querySelector("[data-frequency-panel]");
        this.sellingPlanInput = this.querySelector("[data-selling-plan-input]");
        this.oneTimeChoice = this.querySelector("[data-one-time-choice]");
        this.oneTimeInput = this.oneTimeChoice?.querySelector('input[value="one-time"]');
        this.oneTimePrice = this.querySelector("[data-one-time-price]");
        this.oneTimeComparePrice = this.querySelector("[data-one-time-compare-price]");
        this.subscriptionComparePrice = this.querySelector("[data-subscription-compare-price]");
        this.subscriptionPrice = this.querySelector("[data-subscription-price]");
        this.subscriptionRecurring = this.querySelector("[data-subscription-recurring]");
        this.subscriptionBadge = this.querySelector("[data-subscription-badge]");
        this.productInfoWrapper = this.closest(".m-product-info--wrapper");

        this.addEventListener("change", (event) => this.handleChange(event));
        document.addEventListener("variant:changed", (event) => this.handleVariantChange(event));
        window.addEventListener("load", () => {
          this.bindProductForm();
          this.schedulePurchasePresentation();
        }, { once: true });
        this.observeProductForm();

        setTimeout(() => {
          this.bindProductForm();
          this.render();
        }, 0);
      }

      bindProductForm() {
        const nextButtonText = this.productInfoWrapper?.querySelector(".m-add-to-cart--text");
        if (nextButtonText !== this.buttonText) {
          this.buttonObserver?.disconnect();
          this.buttonObserver = null;
          this.buttonText = nextButtonText;
        }

        this.productFormWrapper = this.productInfoWrapper?.querySelector(".m-product-form--wrapper");
        this.quantityInput = this.productFormWrapper?.querySelector('input[name="quantity"]');
        this.updateQuantitySelector(this.data.variants[this.variantId]);
        this.observeAddToCartLabel();
      }

      observeProductForm() {
        if (!this.productInfoWrapper || this.formObserver) return;

        this.formObserver = new MutationObserver(() => {
          this.bindProductForm();
          this.updatePurchasePresentation();
        });
        this.formObserver.observe(this.productInfoWrapper, { childList: true, subtree: true });
      }

      handleChange(event) {
        if (event.target.matches('input[name^="purchase-type-"]')) {
          const variant = this.data.variants[this.variantId];
          if (event.target.value === "one-time" && variant?.requiresSellingPlan) return;

          this.purchaseType = event.target.value;
          this.render();
        }

        if (event.target === this.frequencySelect) {
          this.selectedPlanName = this.getSelectedPlan()?.name;
          this.syncSellingPlan();
          this.updatePackCards();
          this.schedulePurchasePresentation();
        }
      }

      handleVariantChange(event) {
        const nextVariantId = String(event.detail?.variant?.id || "");
        if (!this.data.variants[nextVariantId]) return;

        this.variantId = nextVariantId;
        this.render();
      }

      render() {
        const variant = this.data.variants[this.variantId];
        const hasPlans = variant && variant.subscriptionEligible && variant.plans.length > 0;
        this.updateQuantitySelector(variant);

        if (!hasPlans) {
          this.purchaseType = "one-time";
          this.hidden = true;
          this.setOneTimeAvailability(false);
          this.clearSellingPlan();
          this.updatePackCards();
          this.schedulePurchasePresentation();
          return;
        }

        this.hidden = false;
        if (variant.requiresSellingPlan) this.purchaseType = "subscription";
        this.setOneTimeAvailability(!variant.requiresSellingPlan);
        this.oneTimePrice.textContent = variant.oneTimePrice;
        this.oneTimeComparePrice.textContent = variant.oneTimeCompareAtPrice || "";
        this.oneTimeComparePrice.hidden = !variant.oneTimeCompareAtPrice;
        this.renderFrequencies(variant.plans);
        this.renderSubscriptionPricing();

        this.querySelectorAll(".candle-subscription__choice").forEach((choice) => {
          const input = choice.querySelector("input");
          const isSelected = !input.disabled && input.value === this.purchaseType;
          input.checked = isSelected;
          choice.classList.toggle("is-selected", isSelected);
        });

        this.frequencyPanel.hidden = this.purchaseType !== "subscription";
        this.classList.toggle("is-subscription-selected", this.purchaseType === "subscription");
        this.updatePackCards();
        this.syncSellingPlan();
        this.schedulePurchasePresentation();
      }

      updatePackCards() {
        this.productInfoWrapper
          ?.querySelectorAll(".candle-pack-picker [data-pack-saving]")
          .forEach((element) => {
            const packValue = element.closest("[data-value]")?.dataset.value;
            const packVariant = this.findPackVariant(packValue);
            const matchingPlan = this.findMatchingPlan(packVariant, this.getSelectedPlan());
            const savingsLabel = this.purchaseType === "subscription"
              ? matchingPlan?.discountLabel
              : packVariant?.oneTimeDiscountLabel;

            element.textContent = savingsLabel || "";
            element.hidden = !savingsLabel;
          });
      }

      findPackVariant(packValue) {
        const selectedVariant = this.data.variants[this.variantId];
        const packOptionIndex = this.data.packOptionIndex;
        if (!selectedVariant || packOptionIndex === null || !packValue) return null;

        return Object.values(this.data.variants).find((variant) =>
          variant.options.every((optionValue, index) =>
            optionValue === (index === packOptionIndex ? packValue : selectedVariant.options[index])
          )
        );
      }

      findMatchingPlan(variant, selectedPlan) {
        if (!variant?.subscriptionEligible || !variant.plans.length) return null;
        if (!selectedPlan) return variant.plans[0];

        return variant.plans.find((plan) => plan.name === selectedPlan.name) || variant.plans[0];
      }

      updateQuantitySelector(variant) {
        if (!variant || !this.productFormWrapper) return;

        const isFixedMultiPack = this.data.fixedPackQuantity && variant.subscriptionEligible;
        this.productFormWrapper.classList.toggle("candle-product-form--fixed-pack", isFixedMultiPack);

        if (isFixedMultiPack && this.quantityInput && String(this.quantityInput.value) !== "1") {
          this.quantityInput.value = 1;
          this.quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      setOneTimeAvailability(isAvailable) {
        if (this.oneTimeChoice) this.oneTimeChoice.hidden = !isAvailable;
        if (this.oneTimeInput) this.oneTimeInput.disabled = !isAvailable;
      }

      renderFrequencies(plans) {
        const selectedPlanId = String(this.frequencySelect.value || "");
        const selectedPlanName = this.selectedPlanName || this.getSelectedPlan()?.name;
        this.frequencySelect.innerHTML = "";

        plans.forEach((plan) => {
          const option = document.createElement("option");
          option.value = plan.id;
          option.textContent = plan.name;
          this.frequencySelect.appendChild(option);
        });

        if (plans.some((plan) => String(plan.id) === selectedPlanId)) {
          this.frequencySelect.value = selectedPlanId;
        } else if (selectedPlanName) {
          const matchingPlan = plans.find((plan) => plan.name === selectedPlanName);
          if (matchingPlan) this.frequencySelect.value = matchingPlan.id;
        }

        this.selectedPlanName = this.getSelectedPlan()?.name;
      }

      getSelectedPlan() {
        const variant = this.data.variants[this.variantId];
        const selectedPlanId = String(this.frequencySelect?.value || "");
        return variant?.plans.find((plan) => String(plan.id) === selectedPlanId) || null;
      }

      renderSubscriptionPricing() {
        const plan = this.getSelectedPlan();
        if (!plan) return;

        this.subscriptionComparePrice.textContent = plan.compareAtPrice || "";
        this.subscriptionComparePrice.hidden = !plan.compareAtPrice;
        this.subscriptionPrice.textContent = plan.price;
        this.subscriptionBadge.textContent = plan.discountLabel || "";
        this.subscriptionBadge.hidden = !plan.discountLabel;

        const hasRecurringChange = plan.recurringPriceCents !== plan.priceCents;
        if (!hasRecurringChange) {
          this.subscriptionRecurring.textContent = "";
          this.subscriptionRecurring.hidden = true;
          return;
        }

        const count = plan.firstStageOrderCount;
        const template = count === 1
          ? this.dataset.thenPriceAfterDeliveryTemplate
          : count
            ? this.dataset.thenPriceAfterDeliveriesTemplate
            : this.dataset.thenPriceTemplate;
        this.subscriptionRecurring.textContent = template
          .replace("__PRICE__", plan.recurringPrice)
          .replace("__COUNT__", count || "");
        this.subscriptionRecurring.hidden = false;
      }

      syncSellingPlan() {
        if (this.purchaseType === "subscription") {
          const selectedPlan = this.getSelectedPlan();
          if (!selectedPlan) {
            this.clearSellingPlan();
            return;
          }

          this.sellingPlanInput.value = selectedPlan.id;
          this.sellingPlanInput.disabled = false;
          this.renderSubscriptionPricing();
          return;
        }

        this.clearSellingPlan();
      }

      clearSellingPlan() {
        this.sellingPlanInput.value = "";
        this.sellingPlanInput.disabled = true;
      }

      scheduleAddToCartLabel() {
        this.updateAddToCartLabel();
        requestAnimationFrame(() => this.updateAddToCartLabel());
      }

      schedulePurchasePresentation() {
        this.updatePurchasePresentation();
        requestAnimationFrame(() => this.updatePurchasePresentation());
        setTimeout(() => this.updatePurchasePresentation(), 50);
      }

      updatePurchasePresentation() {
        this.updateHeadlinePrice();
        this.updateAddToCartLabel();
      }

      updateHeadlinePrice() {
        const variant = this.data.variants[this.variantId];
        const priceBlock = this.productInfoWrapper?.querySelector(".main-product__block-price");
        const priceWrapper = priceBlock?.querySelector(".m-price");
        if (!variant || !priceWrapper) return;

        const selectedPlan = this.getSelectedPlan();
        const isSubscription = this.purchaseType === "subscription" && selectedPlan;
        const price = isSubscription ? selectedPlan.price : variant.oneTimePrice;
        const comparePrice = isSubscription
          ? selectedPlan.compareAtPrice
          : variant.oneTimeCompareAtPrice;
        const discountLabel = isSubscription
          ? selectedPlan.discountLabel
          : variant.oneTimeDiscountLabel;
        const isOnSale = Boolean(comparePrice && discountLabel);
        const salePrice = priceWrapper.querySelector(".m-price-item--sale");
        const regularPrice = priceWrapper.querySelector(".m-price__regular .m-price-item--regular");
        const compareAtPrice = priceWrapper.querySelector(".m-price__sale .m-price-item--regular");
        const saleBadge = priceWrapper.querySelector(".m-price__badge-sale");
        const saleBadgeText = saleBadge?.querySelector(".m-product-tag--sale");

        priceWrapper.classList.toggle("m-price--on-sale", isOnSale);

        if (salePrice && salePrice.textContent.trim() !== price) {
          salePrice.textContent = price;
        }
        if (regularPrice && regularPrice.textContent.trim() !== price) {
          regularPrice.textContent = price;
        }
        if (compareAtPrice && compareAtPrice.textContent.trim() !== (comparePrice || price)) {
          compareAtPrice.textContent = comparePrice || price;
        }
        if (saleBadge) {
          saleBadge.hidden = !isOnSale;
        }
        if (saleBadgeText && saleBadgeText.textContent.trim() !== (discountLabel || "")) {
          saleBadgeText.textContent = discountLabel || "";
        }
      }

      observeAddToCartLabel() {
        if (!this.buttonText || this.buttonObserver) return;

        this.buttonObserver = new MutationObserver(() => this.updateAddToCartLabel());
        this.buttonObserver.observe(this.buttonText, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      updateAddToCartLabel() {
        const variant = this.data.variants[this.variantId];
        if (!variant || !variant.available || !this.buttonText) return;

        const selectedPlan = this.getSelectedPlan();
        const isSubscription = this.purchaseType === "subscription" && selectedPlan;
        const price = isSubscription
          ? selectedPlan.price
          : variant.oneTimePrice;
        const comparePrice = isSubscription
          ? selectedPlan.compareAtPrice
          : variant.oneTimeCompareAtPrice;
        const signature = `${comparePrice || ""}|${price}`;
        const renderedCurrentPrice = this.buttonText.querySelector("[data-atc-current-price]")?.textContent;
        const renderedComparePrice = this.buttonText.querySelector("[data-atc-compare-price]")?.textContent || "";
        if (
          this.buttonText.dataset.subscriptionPriceSignature === signature &&
          renderedCurrentPrice === price &&
          renderedComparePrice === (comparePrice || "")
        ) return;

        this.buttonText.dataset.subscriptionPriceSignature = signature;
        this.buttonText.replaceChildren(document.createTextNode("Add to Cart – "));

        if (comparePrice && comparePrice !== price) {
          const comparePriceElement = document.createElement("s");
          comparePriceElement.className = "m-atc-price__compare";
          comparePriceElement.dataset.atcComparePrice = "";
          comparePriceElement.textContent = comparePrice;
          this.buttonText.append(comparePriceElement);
        }

        const currentPriceElement = document.createElement("span");
        currentPriceElement.className = "m-atc-price__current";
        currentPriceElement.dataset.atcCurrentPrice = "";
        currentPriceElement.textContent = price;
        this.buttonText.append(currentPriceElement);
      }
    }
  );
}
